import { QRCodeSVG } from 'qrcode.react';
import { getCustomerBasedProfile } from 'app/main/apps/common/AuraFunctions';
import {
  boxCountText,
  customerText,
  fitText,
  formatBoxCode,
  isInventoryLabel,
  labelDate,
  orderNumberText,
  productLine,
  qrValue,
  vasText,
} from './boxLabel';

/**
 * PGSS-5824: one 6" wide × 4" tall label per box, one per printed page, in the layout of the label PSG uses today
 * (order number and decedent, box count and customer, date and INV, the product on one line and its shape, with dotted
 * lines between). PGSS-5863: the top two rows run the full width, the QR code fills the right side below them, and each
 * group sits in the same box. From PGSS-5748:
 * - a QR code of the box code with the code printed under it, for scanning at the supplier, at PSG and onto the truck;
 * - "USA VAS" / "PLANT VAS" at the bottom for an order with VAS.
 * The customer is the code unless PSG's setting says the name (the API decides, see customerText).
 */
// A hair under the 4in page: a block of exactly the page height can round past it in Chrome and push the page break
// onto a blank page, which on a thermal roll wastes a label after every box.
const GAP = '0.07in';
const LABEL_STYLE = {
  width: '6in',
  height: '3.98in',
  boxSizing: 'border-box',
  border: '2px solid #000',
  padding: '0.09in',
  display: 'flex',
  flexDirection: 'column',
  gap: GAP,
  background: '#fff',
  color: '#000',
  fontFamily: 'Arial, Helvetica, sans-serif',
  // Explicit, not the page's 1.4: every row's height is then known, and the label is sized to fit (review of #2743).
  lineHeight: 1.15,
  overflow: 'hidden',
  pageBreakInside: 'avoid',
  breakInside: 'avoid',
};

const CELL = { flexShrink: 0, minWidth: 0, boxSizing: 'border-box', border: '2px solid #000', padding: '0.05in 0.08in' };
/** Holds the 2.23in QR code and its white margin, with room for the box's border. */
const QR_PANEL_WIDTH = '2.35in';
const QR_PX = 214;
/** Text that never wraps past `lines` lines, so a long value can't push the bottom of the label off the page. */
const clamp = (lines) => ({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: lines,
  overflow: 'hidden',
  overflowWrap: 'anywhere',
});
/** The font size and line limit for a value that shrinks to fit (see fitText), as a style. */
const fitted = (text, options) => {
  const { fontPx, lines } = fitText(text, options);
  return { fontSize: `${fontPx}px`, ...clamp(lines) };
};
const ADDRESS_FIT = { basePx: 9, charsPerLine: 62, lines: 2, minPx: 7 };
// Each box holds about these many bold characters a line at each size.
const HEADLINE_FIT = { basePx: 28, charsPerLine: 32, lines: 1, minPx: 12 };
const CUSTOMER_FIT = { basePx: 20, charsPerLine: 16, lines: 2, minPx: 10 };
const PRODUCT_FIT = { basePx: 20, charsPerLine: 26, lines: 3, minPx: 10 };
const SHAPE_FIT = { basePx: 18, charsPerLine: 28, lines: 1, minPx: 9 };

function BoxLabel({ label, companyName, companyAddress, containerNumber, isPrinting, isLast }) {
  const vas = vasText(label?.vas_location);
  const code = qrValue(label);
  const order = orderNumberText(label);
  const headline = `${order} ${label?.decedents || ''}`.trim();
  const customer = customerText(label);
  const product = productLine(label);
  // A long count ("112/136") mustn't squeeze the customer beside it.
  const boxCount = boxCountText(label);
  return (
    <div
      className="box-label"
      data-testid="box-label"
      style={{
        ...LABEL_STYLE,
        // PGSS-5869: the outline prints too, as on screen.
        ...(isPrinting ? { pageBreakAfter: isLast ? 'auto' : 'always', breakAfter: isLast ? 'auto' : 'page' } : {}),
      }}
    >
      <div style={{ ...CELL, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.08in' }}>
        <img src={getCustomerBasedProfile.companyLogo} alt={companyName || 'logo'} style={{ height: '0.27in', objectFit: 'contain' }} />
        <div data-testid="box-label-company" style={{ flex: 1, minWidth: 0, textAlign: 'center', lineHeight: 1.15 }}>
          <div style={{ fontSize: '12px', fontWeight: 800, ...clamp(1) }}>{companyName || ''}</div>
          <div data-testid="box-label-company-address" style={fitted(companyAddress, ADDRESS_FIT)}>{companyAddress || ''}</div>
        </div>
        {/* PGSS-5748 part 2b: a staff print from an order can span containers, so each label may carry its own. */}
        <div data-testid="box-label-container" style={{ flexShrink: 0, fontSize: '16px', fontWeight: 700, textAlign: 'right' }}>
          {label?.container_number || containerNumber || ''}
        </div>
      </div>

      {/* The decedent is what a crew checks the box against, so a long name shrinks to fit rather than being cut. */}
      <div style={CELL}>
        <div data-testid="box-label-headline" style={{ fontWeight: 800, ...fitted(headline, HEADLINE_FIT) }}>
          <span data-testid="box-label-order">{order}</span> <span data-testid="box-label-decedent">{label?.decedents}</span>
        </div>
      </div>

      <div data-testid="box-label-body" style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: `1fr ${QR_PANEL_WIDTH}`, gap: GAP }}>
        <div style={{ minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', gap: GAP }}>
          <div style={{ flexShrink: 0, display: 'flex', gap: GAP }}>
            <div data-testid="box-label-box" style={{ ...CELL, display: 'flex', alignItems: 'center', fontSize: boxCount.length > 5 ? '26px' : '36px', fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap' }}>{boxCount}</div>
            <div style={{ ...CELL, flex: 1, flexShrink: 1, display: 'flex', alignItems: 'center' }}>
              <div data-testid="box-label-customer" style={{ minWidth: 0, fontWeight: 800, ...fitted(customer, CUSTOMER_FIT) }}>{customer}</div>
            </div>
          </div>
          <div style={{ ...CELL, display: 'flex', alignItems: 'center', gap: '0.2in' }}>
            <div data-testid="box-label-date" style={{ fontSize: '12px' }}>{labelDate()}</div>
            {/* A box keeps its number, which staff read off the container list; PGSS-5798: "Piece 2 of 6". */}
            <div data-testid="box-label-number" style={{ fontSize: '12px' }}>{`${label?.piece ? 'Piece' : 'Box'} ${label?.box_number ?? ''}`}</div>
            <div data-testid="box-label-qty" style={{ fontSize: '12px' }}>{`QTY ${label?.quantity ?? ''}`}</div>
            {isInventoryLabel(label) && <div data-testid="box-label-inv" style={{ fontSize: '32px', fontWeight: 800, lineHeight: 1 }}>INV</div>}
          </div>
          <div style={{ ...CELL, flex: 1, flexShrink: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '0.03in', overflow: 'hidden' }}>
            <div data-testid="box-label-product" style={{ fontWeight: 700, ...fitted(product, PRODUCT_FIT) }}>{product}</div>
            {label?.subType && <div data-testid="box-label-shape" style={{ fontWeight: 700, ...fitted(label.subType, SHAPE_FIT) }}>{label.subType}</div>}
          </div>
          {vas && (
            <div
              data-testid="box-label-vas"
              style={{ flexShrink: 0, background: '#000', color: '#fff', textAlign: 'center', fontSize: '20px', fontWeight: 800, padding: '0.03in', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}
            >
              {vas}
            </div>
          )}
        </div>

        {/* The QR code takes the height left and shrinks if it must, so the code under it is never cut off. */}
        <div style={{ ...CELL, flexShrink: 1, minHeight: 0, padding: '0.03in', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.02in' }}>
          {code ? (
            <>
              <QRCodeSVG
                value={code}
                size={QR_PX}
                level="M"
                marginSize={4}
                title={`Box code ${formatBoxCode(code)}`}
                data-testid="box-label-qr"
                style={{ flex: '0 1 auto', minHeight: 0, height: 'auto', maxHeight: '100%', width: 'auto', maxWidth: `${QR_PX}px` }}
              />
              <div data-testid="box-label-code" style={{ flexShrink: 0, fontFamily: 'Courier New, monospace', fontSize: '18px', fontWeight: 700, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                {formatBoxCode(code)}
              </div>
            </>
          ) : (
            <div style={{ fontSize: '12px' }}>No box code</div>
          )}
        </div>
      </div>
    </div>
  );
}

function ShipmentLabelContent({ shippingLabel, labelRef, isPrinting }) {
  const labels = shippingLabel?.shipping_label?.orders || [];
  const companyName = shippingLabel?.shipping_label?.company_name;
  const companyAddress = shippingLabel?.shipping_label?.company_address;
  const containerNumber = shippingLabel?.shipping_label?.container_number;
  return (
    <div
      className="shipping-label"
      ref={labelRef}
      style={isPrinting ? {} : { display: 'flex', flexWrap: 'wrap', gap: '24px', padding: '24px' }}
    >
      {labels.map((label, index) => (
        <BoxLabel
          // The index keeps keys unique: a repeated box number ("3,3") is two labels with one code.
          key={`${label.order_id || label.orderId || ''}-${label.box_number || ''}-${label.box_code || ''}-${index}`}
          label={label}
          companyName={companyName}
          companyAddress={companyAddress}
          containerNumber={containerNumber}
          isPrinting={isPrinting}
          isLast={index === labels.length - 1}
        />
      ))}
    </div>
  );
}

export default ShipmentLabelContent;
